import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RotateCcw } from 'lucide-react';
import api from '../../lib/api';
import { CONTENT_GROUPS, useContentReload } from '../../lib/siteContent';

const ContentGroup = ({ group, saved, onSaved }) => {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const value = (fl) => draft[fl.key] ?? saved[fl.key] ?? fl.def;
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/site-content', { values: draft });
      onSaved(data);
      setDraft({});
      toast.success('Textes enregistrés');
    } catch { toast.error('Erreur lors de l’enregistrement'); }
    finally { setSaving(false); }
  };

  const reset = async (fl) => {
    try {
      const { data } = await api.delete(`/admin/site-content/${encodeURIComponent(fl.key)}`);
      onSaved(data);
      setDraft((p) => { const n = { ...p }; delete n[fl.key]; return n; });
      toast.success('Texte d’origine rétabli');
    } catch { toast.error('Impossible de rétablir le texte'); }
  };

  return (
    <section className="border border-[#111111]/10 p-5 md:p-8 bg-white" data-testid={`content-group-${group.title}`}>
      <h3 className="font-serif text-2xl text-[#111111] mb-6">{group.title}</h3>
      <div className="space-y-5">
        {group.fields.map((fl) => (
          <label key={fl.key} className="block">
            <span className="flex items-center justify-between gap-3 mb-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B6B6B]">{fl.label}</span>
              {saved[fl.key] != null && (
                <button type="button" onClick={() => reset(fl)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] hover:text-[#111111]" data-testid={`content-reset-${fl.key}`}>
                  <RotateCcw className="w-3 h-3" /> Texte d’origine
                </button>
              )}
            </span>
            <textarea
              rows={fl.multiline ? 3 : 1}
              value={value(fl)}
              onChange={(e) => setDraft((p) => ({ ...p, [fl.key]: e.target.value }))}
              className="luxury-input-light resize-y"
              data-testid={`content-field-${fl.key}`}
            />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={save} disabled={!dirty || saving} className="px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] disabled:opacity-40 transition-colors" data-testid={`content-save-${group.title}`}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {dirty && <button onClick={() => setDraft({})} className="px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/20 text-[#111111] hover:border-[#111111]">Annuler</button>}
      </div>
    </section>
  );
};

const ContentManager = () => {
  const [saved, setSaved] = useState(null);
  const reloadPublic = useContentReload();
  useEffect(() => { api.get('/site-content').then(({ data }) => setSaved(data)).catch(() => setSaved({})); }, []);
  const onSaved = (data) => { setSaved(data); reloadPublic(); };
  if (!saved) return <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>;
  return (
    <div data-testid="admin-content-manager">
      <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Contenu</span>
      <h2 className="text-3xl md:text-4xl font-serif text-[#111111]">Textes du site</h2>
      <p className="text-sm text-[#6B6B6B] mt-3 mb-10 max-w-2xl leading-relaxed">
        Modifiez les textes des pages redessinées. Un retour à la ligne dans un titre crée une nouvelle ligne sur le site. « Texte d’origine » rétablit le texte approuvé.
      </p>
      <div className="space-y-8">
        {CONTENT_GROUPS.map((g) => <ContentGroup key={g.title} group={g} saved={saved} onSaved={onSaved} />)}
      </div>
    </div>
  );
};

export default ContentManager;
